import { forwardRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { holidayOn } from '@/features/institute/holidays/utils';
import { cn } from '@/lib/utils';
import type { Holiday } from '@/types/holiday';
import type { Weekday } from '@/types/timetable';
import { WEEKDAY_SHORT } from '@/features/teacher/components/timetable/timetableCopy';
import { WEEKDAY_ORDER, addDays, startOfToday, toDayString, weekdayOf } from '../timetableWeek';

export interface TimetableCalendarProps {
  /** The month on show, as its first day. */
  month: Date;
  onMonthChange: (month: Date) => void;
  /** The week currently drawn in the grid, so it can be shaded. */
  weekDates: string[];
  /** The institute's own working days — everything else is a weekly off. */
  workingDays: Weekday[];
  /** The declared calendar. Null while it is still loading. */
  holidays: Holiday[] | null;
  onPickDate: (date: Date) => void;
  className?: string;
}

interface CalendarCell {
  key: string;
  date: Date;
  day: string;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWithinWeek: boolean;
  isWeeklyOff: boolean;
  holiday: Holiday | null;
}

/**
 * The month picker beside the week navigator.
 *
 * Every day it shades, it shades for a reason it read from the institute:
 * a weekly off comes from `workingDays`, and a closure comes from the declared
 * holidays. It used to hard-code Sunday, the second Saturday, and three
 * national holidays by date — which told a school that works alternate
 * Saturdays it was closed, and knew nothing about the closures the office had
 * actually declared.
 */
export const TimetableCalendar = forwardRef<HTMLDivElement, TimetableCalendarProps>(
  function TimetableCalendar(
    {
      month,
      onMonthChange,
      weekDates,
      workingDays,
      holidays,
      onPickDate,
      className,
    },
    ref,
  ) {
  const today = toDayString(startOfToday());
  const working = useMemo(() => new Set(workingDays), [workingDays]);

  const cells = useMemo<CalendarCell[]>(() => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();

    // A whole number of weeks, starting on the Monday on or before the 1st, so
    // the grid never has a ragged first row.
    const firstOfMonth = new Date(year, monthIndex, 1);
    const gridStart = addDays(firstOfMonth, -((firstOfMonth.getDay() + 6) % 7));
    const lastOfMonth = new Date(year, monthIndex + 1, 0);
    const gridEnd = addDays(lastOfMonth, 6 - ((lastOfMonth.getDay() + 6) % 7));
    const dayCount = Math.round((gridEnd.getTime() - gridStart.getTime()) / 86_400_000) + 1;

    const inWeek = new Set(weekDates);

    return Array.from({ length: dayCount }, (_, index) => {
      const date = addDays(gridStart, index);
      const day = toDayString(date);

      return {
        key: day,
        date,
        day,
        dayNumber: date.getDate(),
        isCurrentMonth: date.getMonth() === monthIndex,
        isToday: day === today,
        isWithinWeek: inWeek.has(day),
        isWeeklyOff: !working.has(weekdayOf(date)),
        holiday: holidayOn(holidays, day),
      };
    });
  }, [month, weekDates, working, holidays, today]);

  return (
    <div
      ref={ref}
      className={cn(
        'absolute right-0 mt-2 z-50 w-72 max-w-[calc(100vw-2rem)] rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200/90 dark:border-slate-800 p-4 shadow-2xl flex flex-col gap-3 animate-in fade-in zoom-in-95',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-extrabold text-slate-900 dark:text-white">
          {month.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous month"
            onClick={(e) => {
              e.stopPropagation();
              onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1));
            }}
            className="p-1 text-slate-400 hover:text-slate-800 dark:hover:text-white cursor-pointer transition-colors rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Next month"
            onClick={(e) => {
              e.stopPropagation();
              onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1));
            }}
            className="p-1 text-slate-400 hover:text-slate-800 dark:hover:text-white cursor-pointer transition-colors rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold text-slate-400">
        {WEEKDAY_ORDER.map((day) => (
          <span key={day} className={cn(!workingDays.includes(day) && 'text-rose-500')}>
            {WEEKDAY_SHORT[day].slice(0, 2)}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {cells.map((cell) => {
          const isClosed = cell.isWeeklyOff || cell.holiday !== null;
          const title = cell.holiday
            ? `${cell.holiday.name} — jump to this week`
            : `Jump to the week of ${cell.date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;

          return (
            <button
              key={cell.key}
              type="button"
              title={title}
              aria-current={cell.isToday ? 'date' : undefined}
              onClick={(e) => {
                e.stopPropagation();
                onPickDate(cell.date);
              }}
              className={cn(
                'h-7 w-7 rounded-full flex items-center justify-center font-bold mx-auto transition-colors cursor-pointer text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                !cell.isCurrentMonth && 'text-slate-300 dark:text-slate-600 font-medium',
                cell.isCurrentMonth &&
                  !cell.isToday &&
                  !cell.isWithinWeek &&
                  !isClosed &&
                  'text-slate-800 dark:text-slate-100 hover:bg-indigo-50 dark:hover:bg-slate-800',
                cell.isCurrentMonth &&
                  !cell.isToday &&
                  !cell.isWithinWeek &&
                  isClosed &&
                  'text-rose-500 font-bold hover:bg-rose-50 dark:hover:bg-rose-950/40',
                cell.isWithinWeek &&
                  !cell.isToday &&
                  'bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 font-bold',
                cell.isToday && 'bg-[#4f46e5] text-white font-bold shadow-md shadow-indigo-500/25',
              )}
            >
              {cell.dayNumber}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-xs text-slate-700 dark:text-slate-300 pt-2 border-t border-slate-100 dark:border-slate-800/80 font-bold">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#4f46e5] shrink-0" />
          <span>Today</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-[#f43f5e] shrink-0" />
          <span>Closed</span>
        </div>
      </div>
    </div>
  );
});
